import { Link, useFetcher, useParams, useRouteError } from "@remix-run/react";
import Button from "~/components/Button/Button";
import * as Sentry from "@sentry/remix";
import { useEffect } from "react";

export default function ErrorBoundary() {
  const fetcher = useFetcher();
  const error: any = useRouteError();
  const params = useParams();

  const orderSource = params.orderSource;
  const siteId = params.siteId;

  const homeUrl = orderSource && siteId ? `/${orderSource}/${siteId}` : "/";

  const errorMessage =
    error?.data?.message ||
    error?.data?.error ||
    error?.data?.userMessage ||
    error?.message ||
    error?.statusText ||
    "An unexpected error occurred";

  Sentry.captureRemixErrorBoundaryError(error);

  useEffect(() => {
    if (!(error instanceof Error)) {
      const err = new Error("Error Boundary Error");
      err.name = errorMessage;

      Sentry.withScope(function ErrorBoundaryError(scope) {
        scope.setLevel("error");
        scope.setTag("errorMessage", errorMessage);
        scope.setContext("Error Data", { ...error });
        scope.captureException(err);
      });
    }
  }, [error, errorMessage]);

  return (
    <div className="error-boundary">
      <div className="error-content">
        <h1 className="error-heading">Oops! Something went wrong</h1>
        <p className="error-message">{errorMessage}</p>
        <div className="error-buttons">
          <button
            onClick={() => window.location.reload()}
            className="error-button error-button--primary"
          >
            Try Again
          </button>
          <Link to={homeUrl} className="error-button error-button--secondary">
            Go Home
          </Link>
          <Button
            variant="link"
            onClick={() => {
              Sentry.withScope((scope) => {
                scope.setLevel("fatal");
                scope.setTag("user_initiated_order_reset", "true");
                scope.setTag("errorMessage", errorMessage);
                scope.setContext("Error Data", { ...error });

                const err = new Error("User Reported Issue");
                err.name = errorMessage;
                scope.captureException(err);
              });
              const formData = new FormData();
              formData.set("orderSource", orderSource ?? "");
              formData.set("siteId", siteId ?? "");
              fetcher.submit(formData, {
                action: "/",
                method: "post",
              });
            }}
          >
            Report Issue and start a new order
          </Button>
        </div>
      </div>
    </div>
  );
}
